import { defineCustomElement, defineAsyncComponent, type App } from 'vue';
import { createPinia } from "pinia";

// Стили виджета объявлены ВНУТРИ каждого `.ce.vue` файла (`<style>` блоки):
// `@vitejs/plugin-vue` инлайнит их в опции компонента (`default.styles`)
// и Vue инжектит их в shadow root после резолва async-компонента.
//
// Каждый виджет грузится ленива через dynamic `import()`. Rollup кладёт его
// со всеми зависящими компонентами (BookingModal, MiniCalendar и т.п.) и
// сторами в отдельный chunk. Общие тяжёлые зависимости (vue, pinia, axios,
// date-fns, imask) автоматически выезжают в shared chunk, к которому
// прицеплены оба widget-chunk'а. На странице, где есть только один виджет,
// второй (и его компоненты) НЕ загружается вообще.
//
// Два важных нюанса при работе через `defineAsyncComponent`:
//
//   1) Нельзя передавать loader-функцию напрямую первым аргументом
//      `defineCustomElement` — Vue интерпретирует её как `setup` функционального
//      компонента (см. runtime-core: `defineComponent(options)` → если options
//      это функция, она кладётся в `setup`). Резолв не запускается, shadow root
//      остаётся пустым. Поэтому оборачиваем явно через `defineAsyncComponent`.
//
//   2) `extraOptions` (включая `configureApp` и `styles`) Vue 3.5 мерджит в
//      async-обёртку через `extend(Comp, extraOptions)`. Но после резолва
//      `this._def` ПЕРЕПИСЫВАЕТСЯ resolved-компонентом, и эти опции теряются.
//      Поэтому `configureApp` нельзя оставлять в `extraOptions` — pinia никогда
//      не установится, виджет упадёт на `getActivePinia()` в setup'е. Решение —
//      привязать `configureApp` к самому resolved-модулю внутри loader'а: так он
//      едет вместе с компонентом и точно попадает в `_def` после резолва.

function configureApp(app: App): void {
  app.use(createPinia());
}

function lazyCe(loader: () => Promise<any>) {
  return defineAsyncComponent(() =>
    loader().then((m) => ({ ...m.default, configureApp }))
  );
}

const ipc = defineCustomElement(lazyCe(() => import('@/ImdtAppointmentApp.ce.vue')));
const dpc = defineCustomElement(lazyCe(() => import('@/ImdtDoctorAppointmentApp.ce.vue')));

customElements.define('imdt-appointment-app', ipc);
customElements.define('imdt-doctor-appointment-app', dpc);
